import { Banner } from 'slab'
import { conditionallyLocalizeDate, dateFromISOString } from 'utils'
import { type MemberOnboardingQleMetadata } from 'utils/web_api.schemas'

import { type GrapeAPIErrorType } from './hooks/GrapeAPIError'
import { OnboardingGenericErrorMessage } from './member_onboarding_v2/components/OnboardingGenericErrorMessage'

export type QleErrorCode = 'qle_date_too_early' | 'qle_date_expired' | null

export const QleStartErrorBannerFromMetadata = (
  { metadata, qleErrorCode }:
  {
    metadata: MemberOnboardingQleMetadata,
    qleErrorCode?: 'qle_date_too_early' | 'qle_date_expired'
  }) => {
  if (!qleErrorCode) return null

  const earliestTimeToStart = conditionallyLocalizeDate(dateFromISOString(metadata.earliest_time_to_start))
  const latestTimeToSubmit = conditionallyLocalizeDate(dateFromISOString(metadata.latest_time_to_submit))

  let title: string = ''
  let body: React.ReactNode = ''

  switch (qleErrorCode) {
    case 'qle_date_too_early':
      title = 'Come back later!'
      body = `Your event is too far in the future. You can start shopping on ${earliestTimeToStart}.`
      break
    case 'qle_date_expired':
      title = 'Your window closed'
      body = `The deadline to make changes from this event was on ${latestTimeToSubmit}.`
      break
  }

  return (
    <Banner type='warning' title={title}>
      <p>{body}</p>
    </Banner>
  )
}

// Error banner if someone tries to start QLE too early or too late
export const QleStartErrorBannerFromSubmit = ({ qleError }: { qleError: GrapeAPIErrorType | null }) => {
  if (!qleError) return null

  const { qleErrorCode, message: errorMessage } = qleError

  let title: string = ''
  let body: React.ReactNode = errorMessage
  let bannerType: 'warning' | 'critical' = 'warning'

  // See app/api/web_api/member_onboarding/entities/qle_error.rb
  switch (qleErrorCode) {
    case 'qle_date_too_early':
      title = 'Come back later!'
      break
    case 'qle_date_expired':
      title = 'Your window closed'
      break
    case 'qle_event_date_required':
      title = 'Event date required'
      body = errorMessage ?? 'Please enter the date of your qualifying life event to continue.'
      break
    default:
      title = 'A problem has occurred while processing your request'
      body = <OnboardingGenericErrorMessage error={qleError as unknown as GrapeAPIErrorType} />
      bannerType = 'critical'
  }

  return (
    <Banner type={bannerType} title={title}>
      <p>{body}</p>
    </Banner>
  )
}

// Chooses between the metadata banner and the submit error banner based on provided props
export const QleStartErrorBanner = (
  { qleError, metadata, qleErrorCode }: {
    qleError?: GrapeAPIErrorType | null
    metadata?: MemberOnboardingQleMetadata | null
    qleErrorCode?: 'qle_date_too_early' | 'qle_date_expired' | null
  }
) => {
  // If we have metadata and error code, use the metadata banner
  if (metadata && qleErrorCode) {
    return <QleStartErrorBannerFromMetadata metadata={metadata} qleErrorCode={qleErrorCode} />
  }

  // If no error provided, return null
  if (!qleError) return null

  // Full error object - delegate to the submit error banner
  return <QleStartErrorBannerFromSubmit qleError={qleError as GrapeAPIErrorType} />
}
